import { useEffect } from 'react';
import {
  Transport,
  Metronome,
  Recorder,
  type RecordMode,
} from '@engine/transport';
import type { AudioEngine } from '@engine/audio';
import {
  useTransportStore,
  useSettingsStore,
  useUiStore,
} from '@store/index';

/**
 * Module-level singletons. Transport/Metronome can't exist until the
 * AudioEngine has initialised (we need `ctx` + the limiter bus); we
 * lazy-construct them inside `ensureReady()` on the first call after
 * the audio engine boots, which the CameraStage triggers from the
 * Start overlay.
 */
const ref: {
  transport: Transport | null;
  metronome: Metronome | null;
  recorder: Recorder | null;
  engine: AudioEngine | null;
} = { transport: null, metronome: null, recorder: null, engine: null };

/** Get the active singletons synchronously — only safe to call AFTER
 *  `useTransport(...)` has run + the audio engine has booted. Used by
 *  `useGestureLoop` to read the transport for quantize math. */
export function getTransportSingletons() {
  return ref;
}

/** Imperative API for UI panels. Safe to call even before the engine
 *  has initialised — they no-op when the recorder isn't ready. */
export function startRecording(mode?: RecordMode): void {
  const r = ref.recorder;
  if (!r) return;
  const m = mode ?? useTransportStore.getState().recordMode;
  r.start(m);
}
export function stopRecording(): void {
  ref.recorder?.stop();
}

/** Synchronously construct Transport/Metronome/Recorder against a
 *  ready AudioEngine. SAFE to call multiple times — re-entrant.
 *  Called from CameraStage's onStart handler so the singletons exist
 *  in the SAME tick the AudioContext does, eliminating the race window
 *  where a pluck right after Start bypasses quantize.
 *
 *  Pattern matches Air Drum Set, which constructs transport + metronome
 *  at module load alongside the synth (eager construction). V3 can't do
 *  it at module load — the AudioContext is gated by a user gesture — so
 *  the next-best thing is wiring it into the same user-gesture handler. */
export function ensureTransportSingletons(engine: AudioEngine): boolean {
  if (ref.transport) return true;
  if (!engine.isReady()) return false;
  const ctx = engine.audioContext;
  const bus = engine.outputBus;
  if (!ctx || !bus) return false;

  const ts = useTransportStore.getState();
  const transport = new Transport(ctx, ts.bpm, ts.quantum);
  const metronome = new Metronome(ctx, transport, bus);
  metronome.setVolume(ts.metronomeVolume);
  metronome.onBeat = (beat, isDownbeat) => {
    useTransportStore.getState().setVisualBeat(beat, isDownbeat);
  };

  const recorder = new Recorder({
    ctx,
    audioBus: bus,
    getSources: () => {
      const stage = document.querySelector('.stage') as HTMLElement | null;
      const video = stage?.querySelector('video.stage__video') as HTMLVideoElement | null;
      const overlayCanvases = stage
        ? (Array.from(stage.querySelectorAll('canvas')) as HTMLCanvasElement[])
        : [];
      for (const ov of overlayCanvases) {
        const insideMirror = ov.closest('.stage__overlay--mirrored');
        ov.dataset.mirrored = insideMirror ? 'true' : 'false';
      }
      const mirrored = useSettingsStore.getState().mirrorCamera;
      const rect = stage?.getBoundingClientRect();
      return {
        video: video ?? null,
        overlays: overlayCanvases,
        mirrored,
        stageWidth: rect?.width ?? window.innerWidth,
        stageHeight: rect?.height ?? window.innerHeight,
      };
    },
    onStateChange: (state) => {
      useTransportStore.getState().setRecording(state === 'recording');
    },
  });

  ref.transport = transport;
  ref.metronome = metronome;
  ref.recorder = recorder;
  ref.engine = engine;

  // Apply any store state that was toggled BEFORE singletons existed
  // (e.g. player opened drawer and turned on quantize while engine still
  // booting). Mirrors the per-store-subscribe `apply` below.
  if (metronome.enabled !== ts.metronomeOn) metronome.setEnabled(ts.metronomeOn);
  return true;
}

export function useTransport(engineRef: React.RefObject<AudioEngine | null>) {
  // First-mount safety net: if singletons exist (e.g. HMR'd CameraStage)
  // or the engine has already booted by some other path, sync state.
  // ensureTransportSingletons is the eager path called from onStart.
  useEffect(() => {
    const engine = engineRef.current;
    if (engine) ensureTransportSingletons(engine);
  }, [engineRef]);

  // Sync transport store → live transport/metronome instances.
  useEffect(() => {
    const apply = () => {
      const s = useTransportStore.getState();
      const t = ref.transport;
      const m = ref.metronome;
      if (!t || !m) return;
      if (t.bpm !== s.bpm) t.setBpm(s.bpm);
      if (t.quantum !== s.quantum) t.setTimeSignature(s.quantum);
      m.setVolume(s.metronomeVolume);
      if (m.enabled !== s.metronomeOn) m.setEnabled(s.metronomeOn);
    };
    apply();
    const unsub = useTransportStore.subscribe(apply);
    return unsub;
  }, []);

  // Cut metronome + abort recording on page hide so a backgrounded tab
  // doesn't keep machine-gunning clicks (and so the recording doesn't
  // sit half-written forever).
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        ref.metronome?.setEnabled(false);
        useTransportStore.getState().setMetronomeOn(false);
        if (useTransportStore.getState().recording) {
          ref.recorder?.stop();
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onVis);
    };
  }, []);

  const isUiReady = () => ref.transport !== null;

  // Watch UI consumer effects (purely a presence-of-component hook;
  // currently no-op, but kept so future drawer-driven effects have a
  // place to live without changing the call site).
  void useUiStore;

  return { isUiReady };
}
